<template lang="pug">
v-card
  v-card-title
    v-row(dense align="center")
      v-col
        span {{title}}
      template(v-if="!editing")
        v-col(cols="auto")
          v-text-field(v-model="filters.date__gte" label="Desde" type="date" hide-details outlined dense clearable)
        v-col(cols="auto")
          v-text-field(v-model="filters.date__lte" label="Hasta" type="date" hide-details outlined dense clearable)
        v-col(cols="auto")
          v-btn(@click="fetchData" outlined color="primary") Filtrar
        v-col(cols="auto")
          v-btn(@click="exportData" outlined color="primary") Exportar
        v-col(cols="auto")
          v-btn(v-if="saveAction" @click="downloadTemplate" outlined color="primary")
            v-icon(left) mdi-file-download-outline
            | Plantilla
        v-col(cols="auto")
          BaseImportRealDataForm(v-if="importAction" :action="importAction" @save="onImported" :planId="plan.id" title="Importar datos planificados" warning="Al importar se reemplazan todos los datos planificados actuales del plan por los del archivo.")
        v-col(cols="auto")
          v-btn(v-if="saveAction" color="primary" @click="startEditing")
            v-icon(left) mdi-table-edit
            | Editar / pegar datos
  //- Vista de lectura
  template(v-if="!editing")
    v-simple-table(key="list-estimated-data")
      template(v-slot:default)
        thead(key="list-estimated-data-header")
          tr
            th(v-for="item in tableField" :key="item.field") {{item.text}}
        tbody(key="list-estimated-data-body")
          tr(v-for="estimatedSale in estimatedSales" :key="estimatedSale.id")
            td(v-for="item in tableField" :key="item.field")
              label(v-if="item.field !== 'estimation'") {{estimatedSale[item.field]}}
              label(v-else) {{estimatedSale[item.field] | planEstimation(plan.plan_type)}}
    .text-center.py-4
      v-progress-circular(v-if="loading" :size="50" color="primary" indeterminate :width="7")
      label(v-else-if="!estimatedSales.length") No se encontraron datos registrados
    .text-center.py-4
      v-pagination(v-model="currentPage" :length="paginationLength")
  //- Modo edición (tabla editable + pegar desde Excel)
  template(v-else)
    v-card-text
      v-alert(type="warning" text dense border="left")
        | Edita las celdas o usa <b>Pegar desde Excel</b> para copiar columnas desde tu planilla. Al guardar, estos datos <b>reemplazan por completo</b> el plan planificado actual ({{ editStartCount }} fila{{ editStartCount === 1 ? '' : 's' }}).
      .mb-3
        v-btn.mr-2(color="primary" outlined @click="pasteDialog = true")
          v-icon(left) mdi-clipboard-arrow-down-outline
          | Pegar desde Excel
        v-btn.mr-2(outlined @click="addRow")
          v-icon(left) mdi-plus
          | Agregar fila
        v-btn(outlined color="red" @click="editRows = []" :disabled="!editRows.length") Vaciar
      v-simple-table(key="edit-estimated-data" dense)
        template(v-slot:default)
          thead
            tr
              th(v-for="col in editColumns" :key="col.key") {{col.label}}
              th(style="width:1%")
          tbody
            tr(v-for="(row, idx) in editRows" :key="idx")
              td(v-for="col in editColumns" :key="col.key")
                v-select(
                  v-if="col.type === 'cost' && costNames && costNames.length"
                  v-model="row[col.key]"
                  :items="costNames"
                  hide-details dense outlined
                  :menu-props="{ offsetY: true }")
                v-text-field(
                  v-else-if="col.type === 'date'"
                  v-model="row[col.key]"
                  type="date" hide-details dense outlined)
                v-text-field(
                  v-else-if="col.type === 'number'"
                  v-model="row[col.key]"
                  type="number" hide-details dense outlined)
                v-text-field(
                  v-else
                  v-model="row[col.key]"
                  hide-details dense outlined)
              td
                v-btn(icon color="red" @click="editRows.splice(idx, 1)")
                  v-icon mdi-close
      .text-center.py-4(v-if="!editRows.length")
        label Sin filas. Agrega una fila o pega desde Excel.
      .text-right.mt-4
        v-btn.mr-2(outlined color="primary" @click="cancelEditing" :disabled="saving") Cancelar
        v-btn(color="primary" :loading="saving" @click="attemptSave") Guardar plan planificado
    //- Diálogo de pegado
    v-dialog(v-model="pasteDialog" width="640")
      v-card
        v-card-title Pegar desde Excel
        v-card-text
          p.text--secondary.mb-2
            | Copia las columnas <b>{{ editColumns.map(c => c.label).join(' · ') }}</b> desde tu planilla (en ese orden) y pégalas abajo. Una fila por línea.
          v-textarea(
            v-model="pasteText"
            outlined
            rows="8"
            placeholder="2026-01-01\t100\t1500000\n2026-02-01\t120\t1800000")
          v-checkbox(v-model="pasteReplace" label="Reemplazar las filas actuales" hide-details dense)
        v-card-actions
          v-spacer
          v-btn(text @click="pasteDialog = false") Cancelar
          v-btn(color="primary" @click="applyPaste") Cargar filas
    //- Confirmación de reemplazo
    v-dialog(v-model="confirmDialog" width="480")
      v-card
        v-card-title Confirmar guardado
        v-card-text
          | Vas a reemplazar el plan planificado actual ({{ editStartCount }} fila{{ editStartCount === 1 ? '' : 's' }}) por <b>{{ pendingCount }}</b> fila{{ pendingCount === 1 ? '' : 's' }}. Esta acción no se puede deshacer. ¿Continuar?
        v-card-actions
          v-spacer
          v-btn(text @click="confirmDialog = false" :disabled="saving") Cancelar
          v-btn(color="primary" :loading="saving" @click="doSave") Sí, reemplazar
</template>

<script>
import { mapActions } from 'vuex'
import PaginationMixin from '../../../mixins/PaginationMixin.vue'
import fileDownload from 'js-file-download'
import BaseImportRealDataForm from '../real_data/forms/BaseImportRealDataForm.vue'
import { EventBus } from '../../../../utils/eventBus'

export default {
  mixins: [PaginationMixin],
  components: {
    BaseImportRealDataForm
  },
  props: {
    plan: {
      type: Object,
      required: true
    },
    listAction: {
      type: Function,
      required: true
    },
    exportAction: {
      type: Function,
      required: true
    },
    importAction: {
      type: Function,
      default: null
    },
    saveAction: {
      type: Function,
      default: null
    },
    editColumns: {
      type: Array,
      default: () => []
    },
    costNames: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: ''
    },
    tableField: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      loading: false,
      saving: false,
      filters: {},
      estimatedSales: [],
      editing: false,
      editRows: [],
      editStartCount: 0,
      pasteDialog: false,
      pasteText: '',
      pasteReplace: true,
      confirmDialog: false,
      pendingPayload: null
    }
  },
  computed: {
    pendingCount () {
      return this.pendingPayload ? this.pendingPayload.length : 0
    }
  },
  methods: {
    ...mapActions('estimated_data', ['listEstimatedCost']),
    async fetchData () {
      this.loading = true
      this.estimatedSales = []
      const params = this.getFilters()

      if (this.plan) {
        params.plan_id = this.plan.id
      }
      const response = await this.listAction({ params })
      this.estimatedSales = response.data.results
      this.itemCount = response.data.count
      this.loading = false
    },
    async exportData () {
      const params = this.getFilters()

      if (this.plan) {
        params.plan_id = this.plan.id
      }
      const response = await this.exportAction({ params })
      fileDownload(response.data, 'datos-planificados.xlsx')
    },
    async downloadTemplate () {
      // La exportación usa los mismos encabezados que la importación, así que
      // sirve de plantilla (con datos si los hay, o solo encabezados si no).
      const response = await this.exportAction({ params: { plan_id: this.plan.id } })
      fileDownload(response.data, 'plantilla-datos-planificados.xlsx')
    },
    onImported () {
      this.$toast && this.$toast.success('Datos planificados importados')
      this.currentPage = 1
      this.fetchData()
      this.$emit('updateData')
      EventBus.$emit('updateRealData', this.plan.id)
    },
    async startEditing () {
      // Prefill con todos los datos actuales del plan (sin paginar)
      const params = { plan_id: this.plan.id, page_size: 1000 }
      const response = await this.listAction({ params })
      const results = (response.data && response.data.results) || []
      this.editRows = results.map(r => {
        const row = {}
        for (const col of this.editColumns) {
          row[col.key] = r[col.key]
        }
        return row
      })
      this.editStartCount = results.length
      this.editing = true
    },
    cancelEditing () {
      this.editing = false
      this.editRows = []
      this.pasteText = ''
    },
    addRow () {
      const row = {}
      for (const col of this.editColumns) {
        row[col.key] = ''
      }
      this.editRows.push(row)
    },
    normalizeDate (value) {
      const v = String(value || '').trim()
      if (!v) return v
      // aaaa-mm-dd / aaaa/mm/dd (con o sin cero a la izquierda)
      let m = v.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/)
      if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`
      // aaaa-mm / aaaa/mm
      m = v.match(/^(\d{4})[-/](\d{1,2})$/)
      if (m) return `${m[1]}-${m[2].padStart(2, '0')}-01`
      // dd-mm-aaaa / dd/mm/aaaa
      m = v.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/)
      if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`
      return v
    },
    parseNumber (value) {
      if (typeof value === 'number') return Math.round(value)
      // Convención chilena: '.' separa miles, ',' separa decimales -> se
      // descartan los decimales antes de limpiar (montos son enteros).
      const v = String(value || '').split(',')[0].replace(/[^0-9-]/g, '')
      const n = parseInt(v, 10)
      return isNaN(n) ? 0 : n
    },
    applyPaste () {
      const lines = this.pasteText.split(/\r?\n/).map(l => l.trim()).filter(l => l.length)
      const rows = lines.map(line => {
        const cells = line.split(/\t|;/).map(c => c.trim())
        const row = {}
        this.editColumns.forEach((col, i) => {
          row[col.key] = cells[i] !== undefined ? cells[i] : ''
        })
        return row
      })
      if (this.pasteReplace) {
        this.editRows = rows
      } else {
        this.editRows = this.editRows.concat(rows)
      }
      this.pasteText = ''
      this.pasteDialog = false
    },
    buildPayload () {
      const payload = []
      for (const row of this.editRows) {
        const item = {}
        for (const col of this.editColumns) {
          if (col.type === 'date') {
            item[col.key] = this.normalizeDate(row[col.key])
          } else if (col.type === 'number') {
            item[col.key] = this.parseNumber(row[col.key])
          } else {
            item[col.key] = row[col.key]
          }
        }
        // Ignorar filas sin fecha
        if (!item.date) continue
        payload.push(item)
      }
      return payload
    },
    attemptSave () {
      const payload = this.buildPayload()
      if (!payload.length) {
        const msg = 'No hay filas con fecha para guardar. Agrega o pega datos antes de guardar.'
        this.$toast ? this.$toast.warning(msg) : alert(msg)
        return
      }
      this.pendingPayload = payload
      if (this.editStartCount > 0) {
        // Hay datos previos: pedir confirmación antes de reemplazar
        this.confirmDialog = true
      } else {
        this.doSave()
      }
    },
    parseErrors (response) {
      const d = response && response.data
      if (Array.isArray(d)) {
        const msgs = []
        d.forEach((e, i) => {
          if (e && typeof e === 'object' && Object.keys(e).length) {
            const fila = e.fila || (i + 1)
            const parts = Object.entries(e)
              .filter(([f]) => f !== 'fila')
              .map(([f, v]) => `${f}: ${Array.isArray(v) ? v.join(' ') : v}`)
            if (parts.length) msgs.push(`Fila ${fila} — ${parts.join('; ')}`)
            else if (typeof e === 'string') msgs.push(e)
          } else if (typeof e === 'string') {
            msgs.push(e)
          }
        })
        if (msgs.length) {
          return msgs.slice(0, 5).join(' | ') + (msgs.length > 5 ? ` (y ${msgs.length - 5} más)` : '')
        }
      }
      if (d && d.detail) return d.detail
      return 'No se pudo guardar. Revisa que las fechas y montos sean válidos.'
    },
    async doSave () {
      const payload = this.pendingPayload || this.buildPayload()
      this.saving = true
      const response = await this.saveAction({ planId: this.plan.id, data: payload })
      this.saving = false

      if (response && response.status >= 200 && response.status < 300) {
        this.$toast && this.$toast.success('Plan planificado guardado')
        this.confirmDialog = false
        this.pendingPayload = null
        this.editing = false
        this.editRows = []
        this.currentPage = 1
        this.fetchData()
        this.$emit('updateData')
        EventBus.$emit('updateRealData', this.plan.id)
      } else {
        this.confirmDialog = false
        const msg = this.parseErrors(response)
        this.$toast ? this.$toast.error(msg) : alert(msg)
      }
    }
  },
  created () {
    this.fetchData()
  }
}
</script>
